import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../Layout/Layout';
import api from '../utils/apiClient';
import {
    FiTerminal,
    FiPlay,
    FiPower,
    FiClock,
    FiAlertCircle,
    FiCheck,
    FiLoader,
    FiMaximize2,
    FiMonitor,
    FiRefreshCw
} from 'react-icons/fi';

// Styled Components (using AttackBox design)
const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PageDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 800px;
  line-height: 1.6;
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const VMCard = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  &::before {
    content: '';
    display: block;
    height: 4px;
    background: ${({ vmType, theme }) => 
        vmType === 'kali' ? theme.colors.success : theme.colors.warning};
    width: 100%;
  }
`;

const VMHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const VMTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  font-weight: 600;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ vmType, theme }) => 
        vmType === 'kali' ? theme.colors.success : theme.colors.warning};
  }
`;

const VMContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
`;

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const CredentialsSection = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundDark};
  border: 1px dashed ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const CredentialRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
    padding-bottom: ${({ theme }) => theme.spacing[2]};
  }
`;

const CredentialLabel = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CredentialValue = styled.span`
  font-family: 'Roboto Mono', monospace;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.success};
  font-weight: 500;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const StatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ status, theme }) => {
        switch (status) {
            case 'running':
                return theme.colors.success;
            case 'starting':
                return theme.colors.warning;
            case 'stopping':
                return theme.colors.warning;
            case 'failed':
                return theme.colors.error;
            default:
                return theme.colors.textTertiary;
        }
    }};
  
  ${({ pulse }) => pulse && `
    animation: pulse 1.5s infinite;
    
    @keyframes pulse {
      0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(10, 255, 112, 0.4);
      }
      
      70% {
        transform: scale(1);
        box-shadow: 0 0 0 6px rgba(10, 255, 112, 0);
      }
      
      100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(10, 255, 112, 0);
      }
    }
  `}
`;

const StatusLabel = styled.span`
  font-weight: 500;
  color: ${({ status, theme }) => {
        switch (status) {
            case 'running':
                return theme.colors.success;
            case 'starting':
                return theme.colors.warning;
            case 'stopping':
                return theme.colors.warning;
            case 'failed':
                return theme.colors.error;
            default:
                return theme.colors.textSecondary;
        }
    }};
`;

const VMFrame = styled.div`
  height: 400px;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const IframeContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const PlaceholderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.spacing[4]};
`;

const PlaceholderIcon = styled.div`
  font-size: 64px;
  color: ${({ vmType, theme }) => 
      vmType === 'kali' ? theme.colors.success : theme.colors.warning};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  opacity: 0.6;
`;

const PlaceholderText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 300px;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
`;

const ActionButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background: ${({ variant, theme, vmType }) =>
        variant === 'stop'
            ? theme.colors.error
            : variant === 'secondary'
                ? theme.colors.secondary
                : vmType === 'kali'
                    ? theme.colors.success
                    : theme.colors.warning};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonWide = styled(ActionButton)`
  grid-column: span 2;
  
  @media (max-width: 500px) {
    grid-column: span 1;
  }
`;

const TimeRemainingSection = styled.div`
  margin: ${({ theme }) => theme.spacing[4]} 0;
`;

const TimeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const TimeLabel = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  span {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const TimeBarContainer = styled.div`
  height: 8px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const TimeBar = styled.div`
  height: 100%;
  width: ${({ percentage }) => `${percentage}%`};
  background: ${({ theme, percentage }) =>
        percentage > 50 ? theme.colors.success :
            percentage > 25 ? theme.colors.warning :
                theme.colors.error
    };
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: width 1s ease-in-out;
`;

const LoadingSpinner = styled(FiLoader)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.error}22;
  color: ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const SuccessMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.success}22;
  color: ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const IframeOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[3]};
    background: ${({ theme }) => `${theme.colors.backgroundLight}F2`};
    text-align: center;
    padding: ${({ theme }) => theme.spacing[5]};
    backdrop-filter: blur(2px);
`;

const OverlayTitle = styled.h3`
    margin: 0;
    font-size: ${({ theme }) => theme.fonts.sizes.lg};
    color: ${({ theme }) => theme.colors.textPrimary};
`;

const OverlayDescription = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary};
    max-width: 320px;
    line-height: 1.5;
`;

const OverlayActions = styled.div`
    margin-top: ${({ theme }) => theme.spacing[2]};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`;

const OverlayHint = styled.span`
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
    color: ${({ theme }) => theme.colors.textSecondary};
`;

const OverlayButton = styled.button`
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid ${({ theme }) => theme.colors.primary};
    background: transparent;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    cursor: pointer;
    transition: ${({ theme }) => `all ${theme.transitions.fast}`};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[2]};

    &:hover {
        background: ${({ theme }) => `${theme.colors.primary}15`};
    }
`;

const DEFAULT_SESSION_DURATION = 10800; // seconds (3 hours)
const SESSION_STORAGE_KEYS = {
    kali: 'chief_vm_kali_end_timestamp',
    juice: 'chief_vm_juice_end_timestamp'
};
const MIN_IFRAME_DELAY_MS = 8000;
const IFRAME_EARLY_LOAD_THRESHOLD_MS = 1500;
const MAX_IFRAME_RETRY_ATTEMPTS = 5;
const IFRAME_RETRY_DELAY_MS = 1000;

const pollVMUrl = async (url, vmKey, setVmStates, onReady) => {
    const maxAttempts = 60; // 5 minutes at 5s intervals
    let finalUrl = url;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const response = await fetch(url, { method: 'HEAD', redirect: 'follow' });
            if (response.ok) {
                finalUrl = response.url; // Get the final URL after redirects
                // Update the sessionData with the final URL
                setVmStates(prev => ({
                    ...prev,
                    [vmKey]: {
                        ...prev[vmKey],
                        sessionData: {
                            ...prev[vmKey].sessionData,
                            vm_url: finalUrl
                        }
                    }
                }));
                if (typeof onReady === 'function') onReady(vmKey, true, { resetAttempts: true });
                return;
            }
        } catch (error) {
            // ignore fetch errors
        }
        await new Promise(resolve => setTimeout(resolve, 5000)); // wait 5s
    }
    // Timeout, proceed anyway
    if (typeof onReady === 'function') onReady(vmKey, true, { resetAttempts: true });
};

const getStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

const loadSessionEndTime = (vmKey) => {
    const storage = getStorage();
    if (!storage) return null;
    const raw = storage.getItem(SESSION_STORAGE_KEYS[vmKey]);
    if (!raw) return null;
    const timestamp = Number(raw);
    return Number.isFinite(timestamp) ? timestamp : null;
};

const saveSessionEndTime = (vmKey, timestamp) => {
    const storage = getStorage();
    if (!storage) return;
    storage.setItem(SESSION_STORAGE_KEYS[vmKey], String(timestamp));
};

const clearSessionEndTime = (vmKey) => {
    const storage = getStorage();
    if (!storage) return;
    storage.removeItem(SESSION_STORAGE_KEYS[vmKey]);
};

const VMDashboard = () => {
    const { currentUser } = useAuth();
    const [vmStates, setVmStates] = useState({
        kali: { loading: false, status: null, sessionData: null, error: null, success: null },
        juice: { loading: false, status: null, sessionData: null, error: null, success: null }
    });
    const [timeRemaining, setTimeRemaining] = useState({ kali: null, juice: null });
    const [iframeVisibility, setIframeVisibility] = useState({ kali: false, juice: false });
    const [iframeKeys, setIframeKeys] = useState({ kali: 0, juice: 0 });
    const heartbeatInterval = useRef(null);
    const timerInterval = useRef(null);
    const vmStatusRef = useRef({ kali: null, juice: null });
    const sessionEndTimeRef = useRef({
        kali: loadSessionEndTime('kali'),
        juice: loadSessionEndTime('juice')
    });
    const iframeTimers = useRef({ kali: null, juice: null });
    const iframeStartRef = useRef({ kali: null, juice: null });
    const iframeActiveRef = useRef({ kali: false, juice: false });
    const iframeAttemptRef = useRef({ kali: 0, juice: 0 });

    useEffect(() => {
        fetchVMStatuses();
        setupHeartbeat();
        setupLocalTimer();
        return () => {
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
            if (timerInterval.current) {
                clearInterval(timerInterval.current);
            }
            Object.keys(iframeTimers.current).forEach(key => {
                const timer = iframeTimers.current[key];
                if (timer) {
                    clearTimeout(timer);
                    iframeTimers.current[key] = null;
                }
            });
        };
    }, []);
    
    // Reset timer when VM states change
    useEffect(() => {
        vmStatusRef.current = {
            kali: vmStates.kali.status,
            juice: vmStates.juice.status
        };
        setupLocalTimer();
    }, [vmStates.kali.status, vmStates.juice.status]);

    const setupHeartbeat = () => {
        heartbeatInterval.current = setInterval(() => {
            fetchVMStatuses();
        }, 30000); // Every 30 seconds
    };
    
    const isVmActive = (status) => {
        if (!status) return false;
        const normalized = String(status).trim().toLowerCase();
        return ['running', 'active', 'started', 'ready', 'on', 'available'].includes(normalized);
    };

    const setupLocalTimer = () => {
        // Clear any existing interval
        if (timerInterval.current) {
            clearInterval(timerInterval.current);
        }
        
        timerInterval.current = setInterval(() => {
            setTimeRemaining(prev => {
                const now = Date.now();
                const newState = { ...prev };
                const { kali, juice } = vmStatusRef.current;
                const { kali: kaliEnd, juice: juiceEnd } = sessionEndTimeRef.current;

                if (isVmActive(kali) && kaliEnd) {
                    const remaining = Math.max(0, Math.floor((kaliEnd - now) / 1000));
                    newState.kali = remaining;
                    if (remaining === 0) {
                        sessionEndTimeRef.current.kali = null;
                        clearSessionEndTime('kali');
                    }
                } else if (!isVmActive(kali)) {
                    newState.kali = null;
                    sessionEndTimeRef.current.kali = null;
                    clearSessionEndTime('kali');
                }

                if (isVmActive(juice) && juiceEnd) {
                    const remaining = Math.max(0, Math.floor((juiceEnd - now) / 1000));
                    newState.juice = remaining;
                    if (remaining === 0) {
                        sessionEndTimeRef.current.juice = null;
                        clearSessionEndTime('juice');
                    }
                } else if (!isVmActive(juice)) {
                    newState.juice = null;
                    sessionEndTimeRef.current.juice = null;
                    clearSessionEndTime('juice');
                }

                return newState;
            });
        }, 1000); // Update every second
    };

    const clearIframeTimer = (vmKey) => {
        const timer = iframeTimers.current[vmKey];
        if (timer) {
            clearTimeout(timer);
            iframeTimers.current[vmKey] = null;
        }
    };

    const beginIframePreparation = (vmKey, force = false, { resetAttempts = false } = {}) => {
        if (!force && iframeStartRef.current[vmKey]) {
            return;
        }

        clearIframeTimer(vmKey);
        iframeStartRef.current[vmKey] = Date.now();

        if (resetAttempts) {
            iframeAttemptRef.current[vmKey] = 0;
        }

        setIframeVisibility(prev => ({
            ...prev,
            [vmKey]: false
        }));

        setIframeKeys(prev => ({
            ...prev,
            [vmKey]: Date.now()
        }));

        iframeTimers.current[vmKey] = setTimeout(() => {
            setIframeVisibility(prev => ({
                ...prev,
                [vmKey]: true
            }));
            iframeTimers.current[vmKey] = null;
        }, MIN_IFRAME_DELAY_MS);
    };

    const endIframePreparation = (vmKey) => {
        clearIframeTimer(vmKey);
        iframeStartRef.current[vmKey] = null;
        iframeAttemptRef.current[vmKey] = 0;
        setIframeVisibility(prev => ({
            ...prev,
            [vmKey]: false
        }));
    };

    const handleIframeLoad = (vmKey) => {
        const startedAt = iframeStartRef.current[vmKey];

        if (startedAt === null) {
            setIframeVisibility(prev => ({
                ...prev,
                [vmKey]: true
            }));
            return;
        }

        const elapsed = Date.now() - startedAt;

        if (elapsed < IFRAME_EARLY_LOAD_THRESHOLD_MS) {
            const attempts = iframeAttemptRef.current[vmKey] ?? 0;
            if (attempts < MAX_IFRAME_RETRY_ATTEMPTS) {
                iframeAttemptRef.current[vmKey] = attempts + 1;
                setIframeVisibility(prev => ({
                    ...prev,
                    [vmKey]: false
                }));
                clearIframeTimer(vmKey);
                iframeTimers.current[vmKey] = setTimeout(() => {
                    beginIframePreparation(vmKey, true);
                }, IFRAME_RETRY_DELAY_MS);
                return;
            }
        }

        const remaining = MIN_IFRAME_DELAY_MS - elapsed;

        if (remaining <= 0) {
            clearIframeTimer(vmKey);
            setIframeVisibility(prev => ({
                ...prev,
                [vmKey]: true
            }));
        } else {
            clearIframeTimer(vmKey);
            iframeTimers.current[vmKey] = setTimeout(() => {
                setIframeVisibility(prev => ({
                    ...prev,
                    [vmKey]: true
                }));
                iframeTimers.current[vmKey] = null;
            }, remaining);
        }
    };

    const handleIframeReload = (vmKey) => {
        if (!sessionEndTimeRef.current[vmKey]) {
            return;
        }

        iframeAttemptRef.current[vmKey] = 0;
        beginIframePreparation(vmKey, true, { resetAttempts: true });
    };

    useEffect(() => {
        const syncIframeState = (vmKey, vmData) => {
            const isActive = vmData.sessionData && isVmActive(vmData.status);
            const wasActive = iframeActiveRef.current[vmKey];

            if (isActive && !wasActive) {
                iframeActiveRef.current[vmKey] = true;
                iframeAttemptRef.current[vmKey] = 0;
                beginIframePreparation(vmKey, true, { resetAttempts: true });
            } else if (!isActive && wasActive) {
                iframeActiveRef.current[vmKey] = false;
                endIframePreparation(vmKey);
            } else {
                iframeActiveRef.current[vmKey] = isActive;
            }
        };

        syncIframeState('kali', vmStates.kali);
        syncIframeState('juice', vmStates.juice);
    }, [vmStates.kali.sessionData, vmStates.kali.status, vmStates.juice.sessionData, vmStates.juice.status]);

    const fetchVMStatuses = async () => {
        try {
            const data = await api.vm.getStatus();
            
            if (data.success && data.sessions) {
                const kaliSession = data.sessions.kali;
                const juiceSession = data.sessions.juice_shop;
                
                setVmStates(prev => ({
                    ...prev,
                    kali: { ...prev.kali, sessionData: kaliSession, status: kaliSession?.status },
                    juice: { ...prev.juice, sessionData: juiceSession, status: juiceSession?.status }
                }));

                const now = Date.now();

                setTimeRemaining(prev => {
                    const next = { ...prev };

                    if (isVmActive(kaliSession?.status)) {
                        if (!sessionEndTimeRef.current.kali) {
                            const stored = loadSessionEndTime('kali');
                            const validStored = stored && stored > now ? stored : null;
                            const fallbackEnd = now + DEFAULT_SESSION_DURATION * 1000;
                            const endTime = validStored || fallbackEnd;
                            sessionEndTimeRef.current.kali = endTime;
                            saveSessionEndTime('kali', endTime);
                        }

                        const remaining = Math.max(0, Math.floor((sessionEndTimeRef.current.kali - now) / 1000));
                        next.kali = remaining;
                        if (remaining === 0) {
                            sessionEndTimeRef.current.kali = null;
                            clearSessionEndTime('kali');
                        }
                    } else {
                        sessionEndTimeRef.current.kali = null;
                        clearSessionEndTime('kali');
                        next.kali = null;
                    }

                    if (isVmActive(juiceSession?.status)) {
                        if (!sessionEndTimeRef.current.juice) {
                            const stored = loadSessionEndTime('juice');
                            const validStored = stored && stored > now ? stored : null;
                            const fallbackEnd = now + DEFAULT_SESSION_DURATION * 1000;
                            const endTime = validStored || fallbackEnd;
                            sessionEndTimeRef.current.juice = endTime;
                            saveSessionEndTime('juice', endTime);
                        }

                        const remaining = Math.max(0, Math.floor((sessionEndTimeRef.current.juice - now) / 1000));
                        next.juice = remaining;
                        if (remaining === 0) {
                            sessionEndTimeRef.current.juice = null;
                            clearSessionEndTime('juice');
                        }
                    } else {
                        sessionEndTimeRef.current.juice = null;
                        clearSessionEndTime('juice');
                        next.juice = null;
                    }

                    return next;
                });
            }
        } catch (error) {
            console.error('Error fetching VM statuses:', error);
        }
    };

    const handleLaunch = async (vmType) => {
        const vmKey = vmType === 'kali' ? 'kali' : 'juice';
        
        setVmStates(prev => ({
            ...prev,
            [vmKey]: { ...prev[vmKey], loading: true, error: null, success: null }
        }));

        try {
            let data;
            if (vmType === 'kali') {
                data = await api.vm.launchKali();
            } else {
                data = await api.vm.launchJuiceShop();
            }
            
            if (data.success) {
                setVmStates(prev => ({
                    ...prev,
                    [vmKey]: { 
                        ...prev[vmKey], 
                        sessionData: data.data, 
                        status: data.data.status,
                        success: `${vmType === 'kali' ? 'Kali Linux' : 'Juice Shop'} launched successfully!`
                    }
                }));

                const sessionSeconds = DEFAULT_SESSION_DURATION;
                const sessionEndTime = Date.now() + sessionSeconds * 1000;
                sessionEndTimeRef.current[vmKey] = sessionEndTime;
                saveSessionEndTime(vmKey, sessionEndTime);

                setTimeRemaining(prev => ({
                    ...prev,
                    [vmKey]: sessionSeconds
                }));

                // Poll the VM URL until it's ready (200 status)
                const url = data.data.vm_url || `http://${data.data.public_ip}:${data.data.port || '80'}`;
                pollVMUrl(url, vmKey, setVmStates, beginIframePreparation);

                // Open VM in new window
                openVMWindow(url, vmType);
            } else {
                setVmStates(prev => ({
                    ...prev,
                    [vmKey]: { ...prev[vmKey], error: data.message || data.error || 'Failed to launch VM' }
                }));
            }
        } catch (error) {
            console.error('VM Launch Error:', error);

            // Extract error message from API response
            let errorMessage = 'Network error while launching VM';

            if (error.response && error.response.data) {
                // API returned an error response (e.g., 403, 400, etc.)
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
            } else if (error.message) {
                // Network or other error
                errorMessage = error.message;
            }

            setVmStates(prev => ({
                ...prev,
                [vmKey]: { ...prev[vmKey], error: errorMessage }
            }));
        } finally {
            setVmStates(prev => ({
                ...prev,
                [vmKey]: { ...prev[vmKey], loading: false }
            }));
        }
    };

    const handleStop = async (vmType) => {
        const vmKey = vmType === 'kali' ? 'kali' : 'juice';
        
        setVmStates(prev => ({
            ...prev,
            [vmKey]: { ...prev[vmKey], loading: true, error: null, success: null }
        }));

        try {
            let data;
            if (vmType === 'kali') {
                data = await api.vm.stopKali();
            } else {
                data = await api.vm.stopJuiceShop();
            }
            
            if (data.success) {
                setVmStates(prev => ({
                    ...prev,
                    [vmKey]: { 
                        ...prev[vmKey], 
                        sessionData: null, 
                        status: null,
                        success: `${vmType === 'kali' ? 'Kali Linux' : 'Juice Shop'} stopped successfully!`
                    }
                }));
                
                setTimeRemaining(prev => ({ ...prev, [vmKey]: null }));
                sessionEndTimeRef.current[vmKey] = null;
                clearSessionEndTime(vmKey);
                endIframePreparation(vmKey);

                // Clear success message after 3 seconds
                setTimeout(() => {
                    setVmStates(prev => ({
                        ...prev,
                        [vmKey]: { ...prev[vmKey], success: null }
                    }));
                }, 3000);
            } else {
                setVmStates(prev => ({
                    ...prev,
                    [vmKey]: { ...prev[vmKey], error: data.message || data.error || 'Failed to stop VM' }
                }));
            }
        } catch (error) {
            console.error('VM Stop Error:', error);

            // Extract error message from API response
            let errorMessage = 'Network error while stopping VM';

            if (error.response && error.response.data) {
                // API returned an error response (e.g., 403, 400, etc.)
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
            } else if (error.message) {
                // Network or other error
                errorMessage = error.message;
            }

            setVmStates(prev => ({
                ...prev,
                [vmKey]: { ...prev[vmKey], error: errorMessage }
            }));
        } finally {
            setVmStates(prev => ({
                ...prev,
                [vmKey]: { ...prev[vmKey], loading: false }
            }));
        }
    };

    const openVMWindow = (url, vmType) => {
        const vmWindow = window.open(
            url, 
            `vm_${vmType}`, 
            'width=1200,height=800,scrollbars=yes,resizable=yes'
        );
        
        if (vmWindow) {
            vmWindow.focus();
        } else {
            const vmKey = vmType === 'kali' ? 'kali' : 'juice';
            setVmStates(prev => ({
                ...prev,
                [vmKey]: { ...prev[vmKey], error: 'Please allow popups for this site to access your VM.' }
            }));
        }
    };

    const formatTime = (seconds) => {
        if (!seconds && seconds !== 0) return 'N/A';

        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins}m ${secs}s`;
    };

    const getStatusText = (vmType, status) => {
        const name = vmType === 'kali' ? 'Kali Linux' : 'Juice Shop';
        if (!status) return `${name} Not Running`;
        
        switch (status) {
            case 'running':
                return `${name} Running`;
            case 'starting':
                return `Starting ${name}...`;
            case 'stopping':
                return `Stopping ${name}...`;
            default:
                return `${name} Ready`;
        }
    };

    const renderVMCard = (vmType, title, description) => {
        const vmKey = vmType === 'kali' ? 'kali' : 'juice';
        const vm = vmStates[vmKey];
        const time = timeRemaining[vmKey];
        const isActive = vm.sessionData && isVmActive(vm.status);
        const showIframe = iframeVisibility[vmKey];

        return (
            <VMCard vmType={vmType}>
                <VMHeader>
                    <VMTitle vmType={vmType}>
                        {vmType === 'kali' ? <FiTerminal /> : <FiMonitor />}
                        {title}
                    </VMTitle>
                </VMHeader>

                <VMContent>
                    {vm.error && (
                        <ErrorMessage>
                            <FiAlertCircle size={18} />
                            {vm.error}
                        </ErrorMessage>
                    )}

                    {vm.success && (
                        <SuccessMessage>
                            <FiCheck size={18} />
                            {vm.success}
                        </SuccessMessage>
                    )}

                    <StatusSection>
                        <StatusIndicator>
                            <StatusDot
                                status={vm.status || 'stopped'}
                                pulse={isActive}
                            />
                            <StatusLabel status={vm.status || 'stopped'}>
                                {getStatusText(vmType, vm.status)}
                            </StatusLabel>
                        </StatusIndicator>
                    </StatusSection>

                    {isActive && vm.sessionData.password && (
                        <CredentialsSection>
                            <CredentialRow>
                                <CredentialLabel>Password:</CredentialLabel>
                                <CredentialValue>{vm.sessionData.password}</CredentialValue>
                            </CredentialRow>
                            {vm.sessionData.port && (
                                <CredentialRow>
                                    <CredentialLabel>Port:</CredentialLabel>
                                    <CredentialValue>{vm.sessionData.port}</CredentialValue>
                                </CredentialRow>
                            )}
                        </CredentialsSection>
                    )}

                    <VMFrame>
                        {isActive ? (
                            <IframeContainer>
                                <StyledIframe
                                    key={`${vmKey}-${iframeKeys[vmKey]}`}
                                    src={vm.sessionData.vm_url || `http://${vm.sessionData.public_ip}:${vm.sessionData.port || '80'}`}
                                    allowFullScreen
                                    title={title}
                                    onLoad={() => handleIframeLoad(vmKey)}
                                    style={{ visibility: showIframe ? 'visible' : 'hidden' }}
                                />
                                {!showIframe && (
                                    <IframeOverlay>
                                        <LoadingSpinner size={28} />
                                        <OverlayTitle>Preparing {title}</OverlayTitle>
                                        <OverlayDescription>
                                            Your environment is booting. This can take up to a minute based on current lab load.
                                        </OverlayDescription>
                                        <OverlayActions>
                                            <OverlayButton type="button" onClick={() => handleIframeReload(vmKey)}>
                                                <FiRefreshCw size={16} />
                                                Reload Lab View
                                            </OverlayButton>
                                            <OverlayHint>
                                                We&apos;re auto-checking for you. Prefer a separate tab? Use &quot;Open in New Window&quot; below.
                                            </OverlayHint>
                                        </OverlayActions>
                                    </IframeOverlay>
                                )}
                            </IframeContainer>
                        ) : (
                            <PlaceholderContent>
                                <PlaceholderIcon vmType={vmType}>
                                    {vmType === 'kali' ? <FiTerminal /> : <FiMonitor />}
                                </PlaceholderIcon>
                                <PlaceholderText>
                                    Your {title} is not running. Launch it to start practicing.
                                </PlaceholderText>
                                <ActionButton
                                    vmType={vmType}
                                    onClick={() => handleLaunch(vmType)}
                                    disabled={vm.loading}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    {vm.loading ? (
                                        <>
                                            <LoadingSpinner size={18} />
                                            Starting...
                                        </>
                                    ) : (
                                        <>
                                            <FiPlay />
                                            Launch {title}
                                        </>
                                    )}
                                </ActionButton>
                            </PlaceholderContent>
                        )}
                    </VMFrame>

                    {isActive && (
                        <>
                            <TimeRemainingSection>
                                <TimeHeader>
                                    <TimeLabel>Session Time Remaining</TimeLabel>
                                    <TimeLabel>
                                        <span>{formatTime(time)}</span>
                                    </TimeLabel>
                                </TimeHeader>
                                <TimeBarContainer>
                                    <TimeBar percentage={time !== null ? (time / DEFAULT_SESSION_DURATION) * 100 : 0} />
                                </TimeBarContainer>
                            </TimeRemainingSection>

                            <ButtonsContainer>
                                <ActionButton
                                    variant="secondary"
                                    onClick={() => openVMWindow(
                                        vm.sessionData.vm_url || `http://${vm.sessionData.public_ip}:${vm.sessionData.port || '80'}`,
                                        vmType
                                    )}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <FiMaximize2 />
                                    Open in New Window
                                </ActionButton>

                                <ActionButton
                                    variant="stop"
                                    onClick={() => handleStop(vmType)}
                                    disabled={vm.loading}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    {vm.loading ? (
                                        <>
                                            <LoadingSpinner size={18} />
                                            Stopping...
                                        </>
                                    ) : (
                                        <>
                                            <FiPower />
                                            Stop {title}
                                        </>
                                    )}
                                </ActionButton>
                            </ButtonsContainer>
                        </>
                    )}

                    {!isActive && (
                        <ButtonWide
                            vmType={vmType}
                            onClick={() => handleLaunch(vmType)}
                            disabled={vm.loading}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                        >
                            {vm.loading ? (
                                <>
                                    <LoadingSpinner size={18} />
                                    Starting...
                                </>
                            ) : (
                                <>
                                    <FiPlay />
                                    Launch {title}
                                </>
                            )}
                        </ButtonWide>
                    )}
                </VMContent>
            </VMCard>
        );
    };

    if (!currentUser) {
        return (
            <Layout>
                <PageContainer>
                    <div style={{ textAlign: 'center', marginTop: '80px' }}>
                        <h2>Please log in to access virtual machines</h2>
                        <p>You need to be authenticated to launch and manage VMs.</p>
                    </div>
                </PageContainer>
            </Layout>
        );
    }

    return (
        <Layout>
            <PageContainer>
                <PageHeader>
                    <PageTitle>
                        <FiMonitor /> Virtual Machine Lab
                    </PageTitle>
                    <PageDescription>
                        Access secure, browser-based virtual environments for hands-on cybersecurity practice.
                        Each environment is isolated with automatic timeout and session management.
                    </PageDescription>
                </PageHeader>

                <ContentContainer>
                    {renderVMCard('kali', 'Kali Linux Attack Box', 'Penetration testing environment')}
                    {renderVMCard('juice-shop', 'Vulnerable Machine', 'Practice web application security')}
                </ContentContainer>
            </PageContainer>
        </Layout>
    );
};

export default VMDashboard;
